import { useState } from "react";
import { NavigateFunction } from "react-router-dom";

import { View, Text, Button, Icon, Dialog } from "./base";

const STORAGE_KEY = "voicerun_welcome_seen";
const NEW_ACCOUNT_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

type Step = {
  icon: string;
  iconColor: string;
  label: string;
  description: string;
  to: string;
};

const steps: Step[] = [
  {
    icon: "bot",
    iconColor: "text-blue-500",
    label: "Create an Agent",
    description: "Define a voice agent and write its conversation logic in Python.",
    to: "/agents/new",
  },
  {
    icon: "checklist",
    iconColor: "text-emerald-500",
    label: "Create an Evaluator",
    description: "Use an LLM to automatically score your agent's calls for quality and accuracy.",
    to: "/evaluators/new",
  },
  {
    icon: "key",
    iconColor: "text-amber-500",
    label: "Create an API Key",
    description: "Authenticate with the VoiceRun API to trigger calls and manage agents programmatically.",
    to: "/profile#api-keys",
  },
];

type WelcomeModalProps = {
  firstName: string;
  navigate: NavigateFunction;
  userCreatedAt?: string;
};

export const WelcomeModal = ({ firstName, navigate, userCreatedAt }: WelcomeModalProps) => {
  const [isOpen, setIsOpen] = useState(() => {
    if (localStorage.getItem(STORAGE_KEY) === "true") return false;
    if (!userCreatedAt) return false;
    const accountAge = Date.now() - new Date(userCreatedAt).getTime();
    if (accountAge >= NEW_ACCOUNT_WINDOW_MS) return false;
    // Mark as seen immediately — if the modal rendered, they saw it
    localStorage.setItem(STORAGE_KEY, "true");
    return true;
  });

  const dismiss = () => {
    setIsOpen(false);
  };

  const handleStepClick = (to: string) => {
    dismiss();
    navigate(to);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={dismiss}
      size="md"
    >
      <View className="p-6 flex flex-col gap-5">
        {/* Heading */}
        <View className="flex flex-col gap-2">
          <Text className="text-xl font-bold text-gray-900 dark:text-white">{firstName ? `Welcome, ${firstName}!` : "Welcome to VoiceRun!"}</Text>
          <Text className="text-sm text-gray-500 dark:text-gray-400 leading-snug">
            VoiceRun is a platform for building AI voice agents. Create an agent, write its conversation logic, deploy it to a phone number, and monitor live
            sessions — all from here.
          </Text>
        </View>

        {/* Quick start */}
        <View className="flex flex-col gap-1">
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 px-1 mb-1">Quick start</Text>
          <View className="flex flex-col gap-2">
            {/* CLI */}
            <a
              href="https://docs.voicerun.com/voicerun-cli/overview/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-row items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 transition-colors text-left w-full group"
            >
              <View className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors shrink-0">
                <Icon
                  name="code"
                  size={18}
                  className="text-purple-500"
                />
              </View>
              <View className="flex flex-col min-w-0 flex-1">
                <Text className="text-sm font-medium text-gray-900 dark:text-white">Start building from your terminal</Text>
                <Text className="text-xs text-gray-500 dark:text-gray-400">
                  Build and deploy agents with the{" "}
                  <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[11px] font-mono">voicerun-cli</code>{" "}
                  command-line tool.
                </Text>
              </View>
              <Icon
                name="open"
                size={14}
                className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
              />
            </a>
            {/* In-app steps */}
            <Text className="text-xs text-gray-400 dark:text-gray-500 px-1 mt-1">Or get started in the dashboard</Text>
            {steps.map((step) => (
              <button
                key={step.to}
                className="flex flex-row items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 transition-colors text-left w-full group"
                onClick={() => handleStepClick(step.to)}
              >
                <View className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors shrink-0">
                  <Icon
                    name={step.icon}
                    size={18}
                    className={step.iconColor}
                  />
                </View>
                <View className="flex flex-col min-w-0 flex-1">
                  <Text className="text-sm font-medium text-gray-900 dark:text-white">{step.label}</Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">{step.description}</Text>
                </View>
                <Icon
                  name="chevron-right"
                  size={14}
                  className="text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                />
              </button>
            ))}
          </View>
        </View>

        {/* Footer */}
        <View className="flex flex-col gap-3">
          <Button
            variant="success"
            size="sm"
            outline
            onClick={dismiss}
            className="justify-center w-full"
          >
            Get Started
          </Button>
          <Text className="text-xs text-gray-400 dark:text-gray-500 text-center">
            Need help?{" "}
            <a
              href="https://docs.voicerun.com/getting-started/quick-start"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Read the docs.
            </a>
          </Text>
        </View>
      </View>
    </Dialog>
  );
};
